import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
  UrlSegment,
} from '@angular/router';
import { PAGE_TYPES } from '@app/constants/app-settings';
import { environment } from '@src/environments/environment';

interface RouteStorageObject {
  snapshot: ActivatedRouteSnapshot;
  handle: DetachedRouteHandle;
  step: string;
}

export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  storedRoutes: { [key: string]: RouteStorageObject } = {};

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return route?.data?.reuseRoute || false;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    const id = this.createIdentifier(route);

    if (route?.data?.reuseRoute && id.length > 0) {
      this.storedRoutes[id] = {
        handle,
        snapshot: route,
        step: route?.data?.step,
      };

      this.cleanStorage(route);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const id = this.createIdentifier(route);
    const storedObject = this.storedRoutes[id];
    const canAttach = !!route.routeConfig && !!storedObject;
    if (!canAttach) {
      return false;
    }

    const paramsMatch = this.compareObjects(
      route.params,
      storedObject.snapshot.params,
    );
    const queryParamsMatch = this.compareObjects(
      route.queryParams,
      storedObject.snapshot.queryParams,
    );

    return paramsMatch && queryParamsMatch;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const id = this.createIdentifier(route);
    if (!route.routeConfig || !this.storedRoutes[id]) {
      return null;
    }

    if (this.storedRoutes[id]?.step === 'LIST_PAGE') {
      try {
        (
          this.storedRoutes[id]?.handle as any
        )?.componentRef?.instance?.b2bMobileWebViewWillRetrieve();
      } catch (e) {
        if (!environment?.production) {
          console.error(e);
        }
      }
    }

    return this.storedRoutes[id].handle;
  }

  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  private createIdentifier(route: ActivatedRouteSnapshot) {
    // Build the complete path from the root to the input route
    const segments: UrlSegment[][] = route.pathFromRoot.map((r) => r.url);
    const subpaths = ([] as UrlSegment[])
      .concat(...segments)
      .map((segment) => segment.path);
    // Result: ${route_depth}-${path}
    return segments.length + '-' + subpaths.join('/');
  }

  private compareObjects(base: any, compare: any): boolean {
    // loop through all properties
    for (const baseProperty in { ...base, ...compare }) {
      // determine if comparrison object has that property, if not: return false
      if (compare.hasOwnProperty(baseProperty)) {
        switch (typeof base[baseProperty]) {
          // if one is object and other is not: return false
          // if they are both objects, recursively call this comparison function
          case 'object':
            if (
              typeof compare[baseProperty] !== 'object' ||
              !this.compareObjects(base[baseProperty], compare[baseProperty])
            ) {
              return false;
            }
            break;
          // if one is function and other is not: return false
          // if both are functions, compare function.toString() results
          case 'function':
            if (
              typeof compare[baseProperty] !== 'function' ||
              base[baseProperty].toString() !== compare[baseProperty].toString()
            ) {
              return false;
            }
            break;
          // otherwise, see if they are equal using coercive comparison
          default:
            // tslint:disable-next-line triple-equals
            if (base[baseProperty] != compare[baseProperty]) {
              return false;
            }
        }
      } else {
        return false;
      }
    }

    // returns true only after false HAS NOT BEEN returned through all loops
    return true;
  }

  cleanStorage(route: ActivatedRouteSnapshot): void {
    let keysToDelete: string[] = [];
    switch (route?.data?.step) {
      case PAGE_TYPES.HOME_PAGE:
        keysToDelete = [
          PAGE_TYPES.FEATURE_HOME_PAGE,
          PAGE_TYPES.LIST_PAGE,
          PAGE_TYPES.DETAIL_PAGE,
          PAGE_TYPES.ACTIVITY_DETAIL_PAGE,
          PAGE_TYPES.GENERIC_PAGE,
        ];
        break;
      case PAGE_TYPES.FEATURE_HOME_PAGE:
        keysToDelete = [
          PAGE_TYPES.LIST_PAGE,
          PAGE_TYPES.DETAIL_PAGE,
          PAGE_TYPES.ACTIVITY_DETAIL_PAGE,
          PAGE_TYPES.GENERIC_PAGE,
        ];
        break;
      case PAGE_TYPES.LIST_PAGE:
        keysToDelete = [
          PAGE_TYPES.DETAIL_PAGE,
          PAGE_TYPES.ACTIVITY_DETAIL_PAGE,
          PAGE_TYPES.GENERIC_PAGE,
        ];
        break;
      case PAGE_TYPES.DETAIL_PAGE:
        keysToDelete = [PAGE_TYPES.CHECKOUT_PAGE];
        break;
      case PAGE_TYPES.ACTIVITY_DETAIL_PAGE:
        keysToDelete = [
          // PAGE_TYPES.GENERIC_PAGE
          PAGE_TYPES.CHECKOUT_PAGE,
        ];
        break;
      case PAGE_TYPES.GENERIC_PAGE:
        // TODO currently not sure
        keysToDelete = [
          // PAGE_TYPES.GENERIC_PAGE
        ];
        break;
    }

    const keys = Object.entries(this.storedRoutes).map(([key, value]) => key);
    keys.forEach((key: string) => {
      if (this.storedRoutes[key]) {
        if (keysToDelete.includes(this.storedRoutes[key]?.step)) {
          (
            this.storedRoutes[key]?.handle as any
          )?.componentRef?.instance?.ngOnDestroy();
          delete this.storedRoutes[key];
        }
      }
    });
  }
}
